import * as R from "ramda";
import { mapObjIndexed } from "ramda";
import React, { useState } from "react";
import * as RR from "react-redux";
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import { DeepPartial } from "redux";
import styled from "styled-components";

import { mkImageUrl } from "../../../actions/course-author/helpers";
import { IFileUploadResponseData } from "../../../api/CourseAuthorApi";
import { AxiosInterceptedResult } from "../../../api/interceptors";
import { IState } from "../../../App/reducer";
import { BackgroundOptions } from "../../../components/pages/CourseAuthor/CourseInstruction/Parts/BackgroundOptions";
import { ClickableToggleDisplay } from "../../../../ts/App/Common/Components/FormComponents";
import { sortColumnRequest } from "../../../actions/course-author/common";
import { getSortableHelperContainer } from "../../../utils";
import { PreferredLocale } from "../../../utils/locales";
import {
  AddInstructionPart,
  AddPartButton as AddInstructionPartAddPartButton,
  ButtonRow as AddInstructionPartButtonsRow,
  Title as AddInstructionPartTitle,
  AddInstructionPartWithDropZone,
} from "../../pages/CourseAuthor/CourseInstruction/Parts/AddInstructionPart";
import { ContentPreviewBox } from "../../pages/CourseAuthor/CourseInstruction/Parts/ContentPreviewBox";
import InstructionPartHeader from "../../pages/CourseAuthor/CourseInstruction/Parts/InstructionPartHeader";
import { isGridPart } from "../../pages/CourseAuthor/CourseInstruction/Parts/util";
import { PartContainer, PartPlaceholder } from "../assessment/Assessment.styles";
import {
  BackgroundSettings,
  ColumnType,
  CourseComponentType,
  CourseUnitView,
  ICourseEditOperations,
  ICourseInstruction,
  ICourseInstructionContentPart,
  IGridPart,
  NullablePartPosition,
} from "../types";
import { CourseInstructionPart } from "./parts/CourseInstructionPart.component";
import { partTitle } from "./parts/CourseInstructionPart.helpers";
import { CourseInstructionPartOperations } from "./parts/CourseInstructionPart.types";
import { ICourseInstructionPartOperations, PreviewInstructionProps } from "./types";
import { useDragAndDrop } from "../../../context/UseDragAndDrop";
import { useTabIdContext } from "../../../context/TabIdContext";

interface ISortableInstructionProps {
  currentLocale: PreferredLocale;
  lookupPart: (index: number) => ICourseInstructionContentPart | null;
  part: ICourseInstructionContentPart;
  partOperations: ICourseInstructionPartOperations;
}

export const SortableInstruction = SortableElement((props: ISortableInstructionProps) => {
  const operations = mapObjIndexed<any, any>(
    fn => fn(props.part),
    props.partOperations
  ) as CourseInstructionPartOperations;

  return isGridPart(props.part) ? (
    <GridPartWrapper
      part={props.part}
      currentLocale={props.currentLocale}
      lookupPart={props.lookupPart}
      partOperations={operations}
      partialPartOperations={props.partOperations}
    />
  ) : (
    <CourseInstructionPart
      contentPart={props.part}
      currentLocale={props.currentLocale}
      isOpen={false}
      partOperations={operations}
    />
  );
});

type LayoutType =
  | [NullablePartPosition, NullablePartPosition]
  | [NullablePartPosition, NullablePartPosition, NullablePartPosition];

const Columns = styled.div<{ columns: ColumnType; layout: LayoutType; imageURL: Nullable<string> }>`
  display: grid;
  border-radius: 4px;
  @media only screen and (max-width: 1285px) {
    grid-template-columns: 1fr;
  }
  grid-template-columns: ${({ columns, layout }) => getGridTemplateColumnResponsive(columns, layout)};
  gap: 1rem;
  background-image: url(${({ imageURL }) => imageURL});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  ${ClickableToggleDisplay} {
    min-width: unset;
  }
`;

const getGridTemplateColumnResponsive = (columns: string, layout: LayoutType): string => {
  switch (columns) {
    case "TwoColumns":
      const [fstFrTwo, sndFrTwo] = layout;
      return `${fstFrTwo}fr ${sndFrTwo}fr`;
    case "ThreeColumns":
      const [fstFrThree, sndFrThree, thrFrThree] = layout;
      return `${fstFrThree}fr ${sndFrThree}fr ${thrFrThree}fr`;
    default:
      return "";
  }
};

const AddInstructionPartWithinGrid = styled(AddInstructionPart)`
  display: flex;
  align-items: center;
  flex-direction: column;

  & + & {
    border-left: 1px solid ${({ theme }) => theme.colors.grey};
    padding: 2rem;
  }

  ${AddInstructionPartTitle} {
    color: ${({ theme }) => theme.colors.grey};
  }

  ${AddInstructionPartButtonsRow} {
    display: grid;
    grid-template-columns: auto auto auto;
    gap: 1rem;
  }

  ${AddInstructionPartAddPartButton} {
    height: 10rem;
    width: 9rem;
  }

  ${AddInstructionPartButtonsRow} svg {
    width: 6.5rem;
  }
`;

interface IGridPartOwnProps {
  currentLocale: PreferredLocale;
  part: IGridPart;
  partOperations: CourseInstructionPartOperations;
  partialPartOperations: ICourseInstructionPartOperations;
  lookupPart: (index: number) => ICourseInstructionContentPart | null;
  uploadFile: (file: File) => Promise<AxiosInterceptedResult<IFileUploadResponseData[]>>;
}

interface IGridColumnProps {
  columnIndex: number;
  partPos: NullablePartPosition;
  addPart: (
    type: CourseComponentType,
    position: number,
    properties: DeepPartial<ICourseInstructionContentPart>
  ) => void;
  currentLocale: PreferredLocale;
  lookupPart: (index: number) => ICourseInstructionContentPart | null;
  partOperations: CourseInstructionPartOperations;
  partialPartOperations: ICourseInstructionPartOperations;
}

const GridColumn: React.FC<IGridColumnProps> = props => {
  const { partPos, addPart, columnIndex, currentLocale, lookupPart, partialPartOperations } = props;

  const part = R.isNil(partPos) ? null : lookupPart(partPos);

  const [isInstructionOpen, setInstructionOpen] = React.useState(false);

  const addPartAndOpenInstruction: typeof addPart = (type, position, properties) => {
    setInstructionOpen(true);
    addPart(type, position, properties);
  };

  if (R.isNil(part) || part.deleted) {
    return (
      <AddInstructionPartWithinGrid
        addPart={addPartAndOpenInstruction}
        includeExplanation={true}
        includeGrids={false}
        position={columnIndex}
        displayOnStart={true}
      />
    );
  }

  const operations = mapObjIndexed<any, any>(fn => fn(part), partialPartOperations) as CourseInstructionPartOperations;

  return (
    <CourseInstructionPart
      currentLocale={currentLocale}
      contentPart={part}
      isOpen={isInstructionOpen}
      partOperations={operations}
    />
  );
};

interface ISortableColumnsProps {
  columns: ColumnType;
  layout:
    | [NullablePartPosition, NullablePartPosition]
    | [NullablePartPosition, NullablePartPosition, NullablePartPosition];
  background: Nullable<BackgroundSettings> | undefined;
  children: React.ReactNode;
}

const SortableColumns = SortableContainer(({ columns, layout, background, children }: ISortableColumnsProps) => (
  <Columns columns={columns} layout={layout} imageURL={mkImageUrl(background)}>
    {children}
  </Columns>
));
const SortableGridColumn = SortableElement((props: IGridColumnProps) => <GridColumn {...props} />);

export const GridPart: React.FC<IGridPartOwnProps> = props => {
  const dispatch = RR.useDispatch();
  const [edit, setEdit] = useState(false);
  const [showBackgroundEdit, editShowBackgroundEdit] = useState(false);
  const toggleEdit = () => setEdit(!edit);
  const { currentLocale, part, partOperations, lookupPart, partialPartOperations, uploadFile } = props;
  const addPart = (columnIndex: number) => (
    type: CourseComponentType,
    // NOTE: The position is ignored to always add new grid parts at the bottom
    position?: number,
    properties: DeepPartial<ICourseInstructionContentPart> = {}
  ) => partOperations.substColumnPart(columnIndex, { type, properties });
  const title = partTitle(part);
  const sortColumn = (sort: any) => {
    if (sort.oldIndex !== sort.newIndex) {
      dispatch(
        sortColumnRequest({
          from: sort.oldIndex,
          itemId: part.id,
          to: sort.newIndex,
        })
      );
    }
  };
  const toggleBackgroundOptions = () => editShowBackgroundEdit(!showBackgroundEdit);

  return (
    <ContentPreviewBox data-testid={`grid-${part.id}`}>
      <InstructionPartHeader
        part={part}
        showBackground={showBackgroundEdit}
        toggleBackgroundOptions={toggleBackgroundOptions}
        deletePart={partOperations.deleteInstructionPart}
        edit={edit}
        expanded={true}
        title={title}
        toggleEdit={toggleEdit}
        toggleExpanded={toggleEdit}
      />

      {showBackgroundEdit && (
        <BackgroundOptions
          imageId={part.content.background?.imageId ?? null}
          patchBackgroundPart={partOperations.changeBackground}
          uploadFile={uploadFile}
        />
      )}
      <SortableColumns
        background={part.content.background}
        columns={part.content.columns.tag}
        layout={part.content.layout.content}
        onSortEnd={sortColumn}
        helperContainer={getSortableHelperContainer()}
        axis="x"
        lockAxis="x"
        useDragHandle={true}
      >
        {part.content.columns.content.map((partPos, idx) => (
          <SortableGridColumn
            key={idx}
            index={idx}
            columnIndex={idx}
            addPart={addPart(idx)}
            partPos={partPos}
            currentLocale={currentLocale}
            lookupPart={lookupPart}
            partOperations={partOperations}
            partialPartOperations={partialPartOperations}
          />
        ))}
      </SortableColumns>
    </ContentPreviewBox>
  );
};

const mapStateToProps = (state: IState) => ({
  uploadFile: state.App.CourseAuthor._.fileUploadApi.uploadFile,
});

export const GridPartWrapper = RR.connect(mapStateToProps)(GridPart);

export interface ISortableInstructionListProps {
  addPart: PreviewInstructionProps["addPart"];
  courseId: Nullable<string>;
  currentLocale: PreferredLocale;
  editOperations: ICourseEditOperations;
  instruction: ICourseInstruction;
  partOperations: ICourseInstructionPartOperations;
  parts: ICourseInstructionContentPart[];
  unitPath: CourseUnitView[];
}

const getIndices = (part: IGridPart) => R.unnest(part.content.columns.content);

export const SortableInstructionList = SortableContainer((props: ISortableInstructionListProps) => {
  const gridParts: IGridPart[] = props.parts.filter(part => !part.deleted).filter(isGridPart);
  const gridIndices = new Set(R.unnest(R.map(getIndices, gridParts)));
  const lookupPart = (index: number) => props.parts.find(part => part.id === index) || null;

  const { tabId: sourceTabId } = useTabIdContext();

  const { handleDragging } = useDragAndDrop();

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, part: ICourseInstructionContentPart) => {
    e.stopPropagation();
    e.dataTransfer.clearData();

    const data = (() => {
      if (part.tag === "grid") {
        return [part, ...part.content.columns.content.map(partPos => (R.isNil(partPos) ? null : lookupPart(partPos)))];
      } else {
        return part;
      }
    })();

    e.dataTransfer.setData(
      "text/plain",
      JSON.stringify({ data, type: "content", courseId: props.courseId, sourceTabId })
    );

    handleDragging(true);
  };

  const handleDragEnd = () => handleDragging(false);

  return (
    <div>
      {props.parts
        .map<ICourseInstructionContentPart>((part, index) => ({
          ...part,
          sort: index,
        }))
        .filter(part => !part.deleted)
        .filter(part => !gridIndices.has(part.id))
        .map(part => (
          <React.Fragment key={part.id}>
            <AddInstructionPartWithDropZone
              dropPosition={part.id}
              dropZoneType="content"
              newInstructionType={null}
              editOperations={props.editOperations}
              path={props.unitPath}
              instructionSlide={props.instruction}
              addPart={props.addPart}
              includeExplanation={true}
              includeGrids={true}
              position={part.id}
              displayOnStart={false}
            />
            <PartContainer
              key={part.id}
              draggable={true}
              // tslint:disable-next-line:jsx-no-lambda
              onDragStart={e => handleDragStart(e, part)}
              onDragEnd={handleDragEnd}
            >
              <SortableInstruction
                currentLocale={props.currentLocale}
                index={part.sort}
                part={part}
                lookupPart={lookupPart}
                partOperations={props.partOperations}
              />
              <PartPlaceholder />
            </PartContainer>
          </React.Fragment>
        ))}
    </div>
  );
});
